import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const useUserPreferences = () => {
  const { token } = useAuth();
  const [preferences, setPreferences] = useState({
    inventoryTabOrder: ['items', 'types', 'services', 'history'],
    reservationsTabOrder: ['reservations', 'services', 'log'],
    dashboardTabOrder: [],
    reportsTabOrder: [],
    theme: 'light',
    language: 'en',
    timezone: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load preferences from server on component mount
  const loadPreferences = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/api/users/user-preferences', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const serverPreferences = response.data.data;

        // Merge with local storage as fallback
        const mergedPreferences = {
          inventoryTabOrder: serverPreferences.inventoryTabOrder ||
                            JSON.parse(localStorage.getItem('inventory-tab-order') || '["items", "types", "services", "history"]'),
          reservationsTabOrder: serverPreferences.reservationsTabOrder ||
                               JSON.parse(localStorage.getItem('reservations-tab-order') || '["reservations", "services", "log"]'),
          dashboardTabOrder: serverPreferences.dashboardTabOrder || [],
          reportsTabOrder: serverPreferences.reportsTabOrder || [],
          theme: serverPreferences.theme || 'light',
          language: serverPreferences.language || 'en',
          timezone: serverPreferences.timezone || null
        };

        setPreferences(mergedPreferences);

        // Update localStorage with server values for offline support
        localStorage.setItem('inventory-tab-order', JSON.stringify(mergedPreferences.inventoryTabOrder));
        localStorage.setItem('reservations-tab-order', JSON.stringify(mergedPreferences.reservationsTabOrder));
      }
    } catch (error) {
      console.error('Failed to load user preferences:', error);
      setError(error.message);

      // Fallback to localStorage if server is unavailable
      const fallbackPreferences = {
        inventoryTabOrder: JSON.parse(localStorage.getItem('inventory-tab-order') || '["items", "types", "services", "history"]'),
        reservationsTabOrder: JSON.parse(localStorage.getItem('reservations-tab-order') || '["reservations", "services", "log"]'),
        dashboardTabOrder: [],
        reportsTabOrder: [],
        theme: 'light',
        language: 'en',
        timezone: null
      };
      setPreferences(fallbackPreferences);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Save preferences to server
  const savePreferences = useCallback(async (updatedPreferences) => {
    if (!token) {
      // Fallback to localStorage if not authenticated
      if (updatedPreferences.inventoryTabOrder) {
        localStorage.setItem('inventory-tab-order', JSON.stringify(updatedPreferences.inventoryTabOrder));
      }
      if (updatedPreferences.reservationsTabOrder) {
        localStorage.setItem('reservations-tab-order', JSON.stringify(updatedPreferences.reservationsTabOrder));
      }
      setPreferences(prev => ({ ...prev, ...updatedPreferences }));
      return;
    }

    try {
      const response = await axios.put('/api/users/user-preferences', updatedPreferences, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const serverPreferences = response.data.data;
        setPreferences(prev => ({ ...prev, ...serverPreferences }));

        // Keep localStorage in sync for offline support
        if (serverPreferences.inventoryTabOrder) {
          localStorage.setItem('inventory-tab-order', JSON.stringify(serverPreferences.inventoryTabOrder));
        }
        if (serverPreferences.reservationsTabOrder) {
          localStorage.setItem('reservations-tab-order', JSON.stringify(serverPreferences.reservationsTabOrder));
        }
      }
    } catch (error) {
      console.error('Failed to save user preferences:', error);
      setError(error.message);

      // Fallback to localStorage if server save fails
      if (updatedPreferences.inventoryTabOrder) {
        localStorage.setItem('inventory-tab-order', JSON.stringify(updatedPreferences.inventoryTabOrder));
      }
      if (updatedPreferences.reservationsTabOrder) {
        localStorage.setItem('reservations-tab-order', JSON.stringify(updatedPreferences.reservationsTabOrder));
      }
      setPreferences(prev => ({ ...prev, ...updatedPreferences }));
    }
  }, [token]);

  // Convenience methods for specific preferences
  const updateInventoryTabOrder = useCallback((newOrder) => {
    savePreferences({ inventoryTabOrder: newOrder });
  }, [savePreferences]);

  const updateReservationsTabOrder = useCallback((newOrder) => {
    savePreferences({ reservationsTabOrder: newOrder });
  }, [savePreferences]);

  const updateTheme = useCallback((theme) => {
    savePreferences({ theme });
  }, [savePreferences]);

  // Load preferences when token changes
  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  return {
    preferences,
    loading,
    error,
    updateInventoryTabOrder,
    updateReservationsTabOrder,
    updateTheme,
    savePreferences,
    refreshPreferences: loadPreferences
  };
};

export default useUserPreferences;